import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { accountApi } from '../services/accountApi';
import { useCurrentUser } from './useCurrentUser';
import { UserNotificationPreferences, NotificationType } from '@troia-v3/types';

/**
 * Sanitize notification preferences com defaults seguros pro `enabled`.
 *
 * ⚠️ IMPORTANTE: `types` é PRESERVADO como-veio — a tela NUNCA inventa lista.
 * Regra única (2026-08-17), igual no backend:
 *   - [...]     → só os tipos listados
 *   - []        → nenhum
 *   - undefined → nenhum (lista nunca gravada; legado pré-migração
 *                 `2026-08-17-002`, que semeia a lista de fábrica)
 *
 * Preencher aqui com a lista de fábrica seria mostrar tipo marcado que o
 * dispatch não entrega enquanto o usuário não salvar — a mesma classe de
 * mentira dos incidentes 2026-08-11 e 2026-08-17. Quem semeia é o backend.
 */
const sanitizePreferences = (prefs: Partial<UserNotificationPreferences> | null | undefined): UserNotificationPreferences => {
  const sanitizeTypes = (types: string[] | undefined): string[] | undefined => {
    if (types === undefined) return undefined;
    return types.filter((t): t is NotificationType => t != null);
  };

  return {
    email: {
      // Canal opt-in: default `false` para casar com o backend
      // (`getUserNotificationPreferences`). Com `true` aqui a tela mostrava
      // "Habilitado" para quem o dispatch considera desligado — mesma classe
      // de mentira do incidente 2026-08-11 (ver `notificationTypeSelection`).
      enabled: prefs?.email?.enabled ?? false,
      ...(prefs?.email?.types !== undefined ? { types: sanitizeTypes(prefs.email.types) } : {}),
    },
    whatsapp: {
      enabled: prefs?.whatsapp?.enabled ?? false,
      ...(prefs?.whatsapp?.types !== undefined ? { types: sanitizeTypes(prefs.whatsapp.types) } : {}),
    },
    push: {
      enabled: prefs?.push?.enabled ?? true,
      ...(prefs?.push?.types !== undefined ? { types: sanitizeTypes(prefs.push.types) } : {}),
    },
    inApp: {
      enabled: prefs?.inApp?.enabled ?? true,
      ...(prefs?.inApp?.types !== undefined ? { types: sanitizeTypes(prefs.inApp.types) } : {}),
    },
    sound: {
      enabled: prefs?.sound?.enabled ?? true,
      ...(prefs?.sound?.types !== undefined ? { types: sanitizeTypes(prefs.sound.types) } : {}),
    }
  };
};

/**
 * Hook to manage user notification preferences
 * Provides current preferences from user data and mutation to update them
 */
export const useNotificationPreferences = () => {
  const queryClient = useQueryClient();
  const { data: user, isLoading } = useCurrentUser();

  // Extract notification preferences from user data
  // Use JSON.stringify for stable memoization to prevent unnecessary re-renders
  const preferencesJson = JSON.stringify(user?.preferences?.notifications || null);

  const preferences = useMemo(() => {
    const parsed = preferencesJson !== 'null' ? JSON.parse(preferencesJson) : null;
    return sanitizePreferences(parsed);
  }, [preferencesJson]);

  // Mutation to update preferences
  const { mutate: updatePreferences, isPending: isSaving, error } = useMutation({
    mutationFn: (newPreferences: UserNotificationPreferences) => {
      // Sanitize before sending to API
      const sanitized = sanitizePreferences(newPreferences);
      return accountApi.updateNotificationPreferences(sanitized);
    },
    onSuccess: (updatedUser) => {
      // Update the cached user data
      queryClient.setQueryData(['current-user'], updatedUser);
    },
  });

  return {
    preferences,
    isLoading,
    updatePreferences,
    isSaving,
    error,
  };
};
